import CalendarFallbackIcon from '@components/shared/CalendarFallbackIcon'
import { hasNationalCalendarFallback } from '@components/shared/calendarFallback'

const statusLabels = {
  DATE_SUGGESTED: 'Data sugerida',
  PROVISIONAL_DATE_SUGGESTED: 'Prazo provisório — 5 dias úteis',
  NO_DEADLINE: 'Sem prazo processual',
  INSUFFICIENT_INFORMATION: 'Faltam dados para calcular',
}

const missingLabels = {
  DATA_DO_EVENTO_DE_PAGAMENTO: 'Data do pagamento que inicia a contagem',
  DATA_DO_EVENTO_QUE_INICIA_PRAZO: 'Data do evento que inicia a contagem',
  PRAZO_REFERENCIADO: 'Prazo mencionado em outro comando ou documento',
  OBRIGACAO_E_DECISAO_DO_PRAZO_PRORROGADO: 'Obrigação prorrogada e decisão anterior que estabeleceu o prazo',
  TEOR_DA_DECISAO_HOMOLOGATORIA: 'Decisão homologatória do acordo',
  CONDICOES_E_VENCIMENTOS_DO_ACORDO: 'Parte responsável, valores, parcelas e vencimentos do acordo',
  PRAZO_JUDICIAL_OU_REGRA_ESPECIFICA: 'Prazo fixado pelo juízo ou regra específica para essa providência',
  QUANTIDADE_OU_REGRA_DE_PRAZO: 'Quantidade ou regra de prazo aplicável',
  DATA_DA_PUBLICACAO_OU_DISPONIBILIZACAO: 'Data de publicação ou disponibilização',
  CALENDARIO_JUDICIAL: 'Calendário judicial aplicável',
  MARCO_INICIAL_OU_UNIDADE: 'Marco inicial e unidade de contagem',
  COMANDO_DECISAO_OU_PRAZO_APLICAVEL: 'Conteúdo da decisão ou ordem que estabelece o prazo',
  TEXTO_VALIDO: 'Texto válido da publicação',
}

function dateLabel(value) {
  if (!value) return 'Aguardando dados'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? String(value) : new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeZone: 'UTC' }).format(date)
}

function calendarLabel(value) {
  if (!value) return 'Não disponível'
  if (value === 'data-expressa-na-pauta') return 'Data expressa na pauta'
  if (value === 'business-days-weekends-only') return 'Somente fins de semana'
  if (value.includes('BRAZIL_NATIONAL_FALLBACK')) return 'Calendário nacional'
  if (value.includes('TEST_BRAZIL_NATIONAL')) return 'Feriados nacionais; sem validação local'
  return value
}

export default function DeadlineAssessmentCard({ assessment, calculatedWithReview = false }) {
  if (!assessment) return null
  const suggestions = assessment.suggestions || []
  const isProvisional = assessment.status === 'PROVISIONAL_DATE_SUGGESTED'
  const suggestedCalendarFallback = suggestions.some((suggestion) => suggestion.recommended === true && suggestion.deadline?.dueDate === assessment.suggestedDueDate
    && hasNationalCalendarFallback(suggestion.deadline))

  return (
    <section className="rules-card deadline-result-section" aria-label="Avaliação de prazos">
      <div className="rules-card-header">
        <div>
          <h3>{calculatedWithReview ? 'Prazo calculado • revisão necessária' : statusLabels[assessment.status] || 'Avaliação de prazos'}</h3>
          <p>{assessment.explanation}</p>
          {isProvisional && (
            <p className="deadline-review-reason">
              Prazo operacional padrão para acompanhar a pendência da publicação. Não representa um vencimento judicial confirmado nem substitui a revisão dos dados ainda necessários.
            </p>
          )}
        </div>
        {assessment.suggestedDueDate && <strong aria-label={isProvisional ? 'Data provisória de acompanhamento' : undefined}>{dateLabel(assessment.suggestedDueDate)}<CalendarFallbackIcon fallbackUsed={suggestedCalendarFallback} /></strong>}
      </div>
      <div className="rules-card-body">
        {suggestions.length > 0 && (
          <div className="deadline-task-list">
            {suggestions.map((suggestion, index) => {
              const deadline = suggestion.deadline || {}
              const isOperational = suggestion.basis === 'OPERATIONAL_FALLBACK' || deadline.deadlineRuleCode === 'PRAZO_PROVISORIO_5_DIAS_UTEIS'
              return (
                <article className="deadline-task-item" key={`${deadline.actionCode}-${index}`}>
                  <div className="deadline-task-header">
                    <h4>{isOperational ? 'Verificar pendência da publicação' : deadline.actionName || deadline.actionCode || 'Providência judicial'}</h4>
                    <span className={`status-badge ${!isOperational && suggestion.recommended ? 'is-info' : 'is-warning'}`}>
                      {isOperational ? 'Prazo operacional provisório' : suggestion.recommended ? 'Sugestão principal' : 'Alternativa condicionada'}
                    </span>
                  </div>
                  <dl className="deadline-task-grid">
                    <div><dt>{isOperational ? 'Data provisória de acompanhamento' : 'Vencimento sugerido'}</dt><dd>{dateLabel(deadline.dueDate)}{suggestion.localDueTime ? ` às ${suggestion.localDueTime.slice(0, 5)} (${suggestion.timeZone || 'horário local'})` : ''}{deadline.dueDate && <CalendarFallbackIcon source={deadline} />}</dd></div>
                    <div><dt>Prazo</dt><dd>{deadline.deadlineUnit === 'FIXED_DATE' ? 'Data fixada na publicação' : deadline.deadlineAmount > 0 ? `${deadline.deadlineAmount} ${deadline.deadlineUnit === 'BUSINESS_DAYS' ? 'dias úteis' : deadline.deadlineUnit === 'CALENDAR_DAYS' ? 'dias corridos' : deadline.deadlineUnit === 'HOURS' ? 'horas' : 'dias'}` : 'Não definido'}</dd></div>
                    <div><dt>Início da contagem</dt><dd>{deadline.deadlineUnit === 'FIXED_DATE' ? 'Data fixada na publicação' : dateLabel(deadline.initialDate)}</dd></div>
                    <div><dt>Calendário</dt><dd>{calendarLabel(deadline.calendarUsed)}</dd></div>
                  </dl>
                  <p>{suggestion.explanation}</p>
                  {deadline.reviewReason && <p className="deadline-review-reason">{deadline.reviewReason}</p>}
                  {!isOperational && suggestion.legalBasis && <a href={suggestion.legalBasis} target="_blank" rel="noreferrer">Consultar fundamento legal</a>}
                </article>
              )
            })}
          </div>
        )}
        {assessment.missingInformation?.length > 0 && (
          <div>
            <strong>Dados ainda necessários</strong>
            <ul>{assessment.missingInformation.map(item => <li key={item}>{missingLabels[item] || item}</li>)}</ul>
          </div>
        )}
      </div>
    </section>
  )
}
